<template>
  <div class="area-graph" :class="{ 'show-labels': showLabels, interactive }"></div>
</template>

<script>
import * as d3 from 'd3';
import * as scale from 'd3-scale';
import gsap from 'gsap';
import CustomEase from '@/vendor/gsap/CustomEase.min';
import CustomBounce from '@/vendor/gsap/CustomBounce.min';
import { random } from '@/utils';
gsap.registerPlugin(CustomEase, CustomBounce);

export default {
  name: 'AreaGraph',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    tilt: {
      type: Number,
      default: 1
    },
    progressive: Boolean,
    holes: {
      type: Number,
      default: 0
    },
    highlighted: {
      type: Object,
      default: null
    },
    showLabels: Boolean,
    interactive: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      initialised: false
    };
  },
  mounted() {
    //this.init();
  },
  watch: {
    highlighted(newVal) {
      this.highlight(newVal);
    }
  },
  destroyed() {},
  methods: {
    init() {
      this.initialised = true;
      const bounds = this.$el.getBoundingClientRect();
      this.bounds = bounds;
      const elements = [];
      this.elements = elements;
      const progressive = this.progressive;
      const root = this.treemap(this.data);
      this.graph = d3.select(this.$el);
      const ease = CustomBounce.create('custom-bounce', {
        strength: 0.1,
        squash: 0.1,
        squashID: 'custom-bounce-squash'
      });
      this.ease = ease;
      this.items = this.graph
        .selectAll('div')
        .data(root.leaves())
        .enter()
        .append('div');

      this.items
        .append('label')
        .attr('class', 'tx-page-subtitle tx-wg-bold')
        .html(function(d) {
          const display = d.data.display || (v => v);
          return display(d.data.label);
        });

      const highlight = this.highlight;
      const select = this.select;
      const $device = this.$device;

      this.items
        .style('left', function(d) {
          return `${d.x0}px`;
        })
        .style('top', function(d) {
          return `${d.y0}px`;
        })
        .style('width', function(d) {
          return `${d.x1 - d.x0}px`;
        })
        .style('height', function(d) {
          return `${d.y1 - d.y0}px`;
        })
        .style('background-color', function(d) {
          return d.data.hole ? '' : d.data.color;
        })
        .each(function(d, i) {
          if (d.data.hole) return;
          elements.push(this);
          this.addEventListener('mouseover', () => highlight(d.data));
          this.addEventListener('mouseleave', () => {
            if (!$device.mobile) highlight(null);
          });
          this.addEventListener('click', () => select(d.data));
          if (progressive) gsap.set(this, { y: -window.innerHeight * 1.1 });
          else {
            gsap.from(this, 2, {
              y: -window.innerHeight * 1.1,
              delay: (root.leaves().length - i) / 6,
              ease
            });
          }
        });
      this.elements.sort((a, b) => {
        const boundsA = a.getBoundingClientRect();
        const boundsB = b.getBoundingClientRect();
        return (boundsA.y - boundsB.y) * 10000 - (boundsA.x - boundsB.x);
      });
    },
    treemap(data) {
      const min = d3.min(data, function(d) {
        return d.fakeValue || d.value;
      });
      const max = d3.max(data, function(d) {
        return d.fakeValue || d.value;
      });

      const dataScale = scale.scaleLinear().domain([min, max]);
      dataScale.range([min * (max / min / (10 / this.tilt)), max]);
      data = data.map(d => ({
        ...d,
        parent: 'origin',
        value: dataScale(d.fakeValue || d.value),
        original: d
      }));
      data.push({ name: 'origin', parent: '', value: '' });

      for (let i = 0; i < this.holes; i++) {
        data.splice(0 + i * 2, 0, {
          name: '',
          parent: 'origin',
          hole: true,
          value: random(dataScale(min) / 2, dataScale(min * 2))
        });
      }

      const root = d3
        .stratify()
        .id(function(d) {
          return d.name;
        })
        .parentId(function(d) {
          return d.parent;
        })(data);

      root.sum(function(d) {
        return +(d.fakeValue || d.value);
      });
      return d3
        .treemap()
        .size([this.bounds.width, this.bounds.height])
        .tile(d3.treemapResquarify.ratio(1))
        .padding(0)(root);
    },
    next(take, options = {}) {
      if (!this.initialised) this.init();
      if (!this.elements.length) return;
      if (take === undefined || take === 'all') take = this.elements.length;
      const els = this.elements.splice(-take, take);
      gsap.to(els, {
        duration: 2,
        y: 0,
        stagger: -0.5,
        ease: this.ease,
        ...options
      });
    },
    highlight(el) {
      if (!this.interactive) return;
      this.$emit('highlight', el);
      this.items.attr('class', function(d) {
        if (!el) return 'active';
        return d.data.id === el.id ? 'highlighted' : 'disabled';
      });
    },
    select(el) {
      if (!this.interactive) return;
      this.$emit('select', el.original);
    }
  }
};
</script>

<style lang="scss">
.area-graph {
  width: 100%;
  height: 100%;
  position: relative;
  &.show-labels {
    div label {
      opacity: 1;
    }
  }
  &.interactive {
    div {
      cursor: pointer;
    }
  }
  div {
    position: absolute;
    top: 0;
    left: 0;
    border-radius: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: opacity.3s;
    &.active,
    &.highlighted {
      opacity: 1;
    }
    &.disabled {
      opacity: 0.3;
    }
    &:hover,
    &.highlighted {
      label {
        opacity: 1;
      }
    }
    label {
      opacity: 0;
      transition: opacity.3s;
      color: $col-white;
      cursor: pointer;
      text-align: center;
      line-height: 85%;
      @media screen and (max-width: $mq-mobile) {
        .tx-cta {
          font-size: 0.75rem;
        }
      }
    }
  }
}
</style>
